import {Entity, IEntity} from "../../wappy/domain/entity";
import {IUserHomeConfiguration, UserHomeConfiguration} from "../home-models/home-model";
import {AppPermissions} from "../../shared/auth/app-permissions";

export interface IUser extends IEntity {
    subjectType?: 'user' | 'prospect';
    userName?: string;
    name: string;
    email: string;
    givenName: string;
    familyName: string;
    roles: string[];
    groups: string[];
    emailVerified: boolean;
    enabled?: boolean;
    configurations: IUserHomeConfiguration[];
    locale?: string;
    hasAnyAcceptedPolicy: boolean;
    hasLatestPolicyAccepted: boolean;
    telephone?: string;
    postalCode?: string;
    prospectCount?: number;
    submissionCount?: number;
}

export class User extends Entity implements IUser {
    private static readonly ADMIN_GROUP = 'Admins';

    name: string;
    email: string;
    givenName: string;
    familyName: string;
    roles: string[];
    groups: string[];
    emailVerified: boolean;
    enabled: boolean;
    configurations: UserHomeConfiguration[] = [];
    locale?: string;
    hasAnyAcceptedPolicy: boolean;
    hasLatestPolicyAccepted: boolean;
    subjectType: 'user' | 'prospect';
    userName?: string;
    telephone?: string;
    postalCode?: string;
    prospectCount: number;
    submissionCount: number;

    constructor(data: IUser) {
        super(data);
        this.name = data.name;
        this.email = data.email;
        this.givenName = data.givenName;
        this.familyName = data.familyName;
        this.roles = [...(data.roles ?? [])];
        this.groups = [...(data.groups ?? [])];
        this.emailVerified = data.emailVerified;
        this.enabled = data.enabled ?? true;
        this.locale = data.locale;
        this.hasAnyAcceptedPolicy = data.hasAnyAcceptedPolicy ?? false;
        this.hasLatestPolicyAccepted = data.hasLatestPolicyAccepted ?? false;
        this.subjectType = data.subjectType ?? 'user';
        this.userName = data.userName;
        this.telephone = data.telephone;
        this.postalCode = data.postalCode;
        this.prospectCount = data.prospectCount ?? 0;
        this.submissionCount = data.submissionCount ?? 0;
    }

    getFullName(): string {
        const fullName = [this.givenName, this.familyName]
            .map(value => typeof value === 'string' ? value.trim() : '')
            .filter(value => value.length > 0)
            .join(' ');

        if (fullName) {
            return fullName;
        }

        const fallback = [this.name, this.email]
            .find(value => typeof value === 'string' && value.trim().length > 0);

        return fallback?.trim() || 'Usuario';
    }

    hasRole(role: string): boolean {
        const normalizedRole = role.trim().toLowerCase();
        return this.roles.some(userRole =>
            userRole.trim().toLowerCase() === normalizedRole
        );
    }

    hasBackOfficeRole(): boolean {
        return this.hasRole(AppPermissions.BACKOFFICE);
    }

    isInGroup(group: string): boolean {
        return this.groups.includes(group);
    }

    isInAdminGroup(): boolean {
        return this.isInGroup(User.ADMIN_GROUP);
    }

    addConfigurations(configurations: UserHomeConfiguration[]): void {
        this.configurations = configurations;
    }

    isProspect(): boolean {
        return this.subjectType === 'prospect';
    }
}
